import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '@/services/auth.service';
import type { User, AppRole, LoginRequest } from '@/types';

export interface AuthState {
  user: User | null;
  loading: boolean;
  userRole: AppRole | null;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export function useAuth(): AuthState {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const fetchCurrentUser = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Error fetching current user:', error);
      // Token invalide, logout
      await authService.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = useCallback(async (credentials: LoginRequest) => {
    setLoading(true);
    try {
      const response = await authService.login(credentials);
      setUser({
        id: response.user.id,
        email: response.user.email,
        role: response.user.role,
        createdAt: new Date().toISOString(),
      });
      // Invalider les queries pour forcer un refresh
      queryClient.invalidateQueries();
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authService.logout();
      setUser(null);
      // Vider le cache de React Query
      queryClient.clear();
    } finally {
      setLoading(false);
    }
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    await fetchCurrentUser();
  }, [fetchCurrentUser]);

  return {
    user,
    loading,
    userRole: user?.role ?? null,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  };
}

export default useAuth;
